"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/shared/ui/buttons/button";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import type { LfgRole } from "@/features/lfg-board/model/board-filters";
import { BoardPostCard } from "@/features/lfg-board/ui/board-post-card";
import type { InviteTeamOption } from "@/features/lfg-board/ui/board-invite-dialog";

interface BoardPostListProps {
  /** null — пояс зрителя ещё неизвестен (гидрация): рисуем загрузку. */
  tzOffset: number | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  items: LfgLftPostResponse[];
  isAuthor: (post: LfgLftPostResponse) => boolean;
  isOwnTeamPost: (post: LfgLftPostResponse) => boolean;
  appliedPostIds: ReadonlySet<string>;
  inviteTeams: InviteTeamOption[];
  actionsDisabled: boolean;
  onClose: (postId: string) => void;
  onApply: (postId: string, role: LfgRole) => void;
  onInvite: (postId: string, teamId: string, message: string) => void;
  onEdit: (post: LfgLftPostResponse) => void;
  onBuildTeam: (teamId: string) => void;
}

/** Четыре состояния списка: загрузка, ошибка, пусто, данные. */
export function BoardPostList(p: BoardPostListProps) {
  const t = useTranslations("LfgBoard");

  if (p.tzOffset === null || p.isLoading)
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-56 w-full rounded-[14px]" />)}
      </div>
    );

  if (p.isError)
    return (
      <div className="flex flex-col items-center gap-3 rounded-[14px] border border-dashed border-border p-10 text-center">
        <p className="text-sm text-muted-foreground">{t("errorLoad")}</p>
        <Button variant="outline" size="sm" onClick={p.onRetry}>{t("retry")}</Button>
      </div>
    );

  if (p.items.length === 0)
    return (
      <p className="rounded-[14px] border border-dashed border-border p-14 text-center text-sm text-muted-foreground">
        {t("empty")}
      </p>
    );

  const tzOffset = p.tzOffset;
  return (
    <ul className="space-y-3">
      {p.items.map((post) => (
        <li key={post.id}>
          <BoardPostCard
            post={post}
            tzOffset={tzOffset}
            isAuthor={p.isAuthor(post)}
            isOwnTeam={p.isOwnTeamPost(post)}
            applied={p.appliedPostIds.has(post.id)}
            inviteTeams={p.inviteTeams}
            actionsDisabled={p.actionsDisabled}
            onApply={(role) => p.onApply(post.id, role)}
            onInvite={(teamId, message) => p.onInvite(post.id, teamId, message)}
            onClose={() => p.onClose(post.id)}
            onEdit={() => p.onEdit(post)}
            onBuild={post.type === "Lft" && post.author?.id ? () => p.onBuildTeam(post.author!.id) : undefined}
          />
        </li>
      ))}
    </ul>
  );
}
